import { Children, useState, type ReactNode } from "react";
import { Badge, Button, Drawer, Dropdown, Space } from "antd";
import { EllipsisOutlined, FilterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "./useIsMobile";

const css = `
.ap-stack .ant-select,.ap-stack .ant-input-search,.ap-stack .ant-input-affix-wrapper,.ap-stack .ant-picker,.ap-stack .ant-input-number{width:100% !important}
.ap-stack .ant-input,.ap-stack .ant-select-selection-search-input{font-size:16px}
`;

/**
 * Barra filtri. Desktop: i filtri sono in riga e vanno a capo. Mobile: la ricerca resta visibile a tutta larghezza;
 * i filtri (a una colonna) sono inline se sono al massimo due, altrimenti stanno in un Drawer dal basso aperto da «Filtri».
 * `active` = numero di filtri attivi (badge sul bottone).
 */
export function FilterBar({ search, children, active = 0, onReset, style }: { search?: ReactNode; children?: ReactNode; active?: number; onReset?: () => void; style?: React.CSSProperties }) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const n = Children.toArray(children).length;
  const resetBtn = onReset ? (
    <Button type="text" onClick={onReset}>
      {t("reset")}
    </Button>
  ) : null;

  if (!mobile) {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16, ...style }}>
        {search}
        {children}
        {resetBtn}
      </div>
    );
  }
  const inline = n <= 2;
  return (
    <div className="ap-stack" style={{ display: "grid", gap: 8, marginBottom: 12, ...style }}>
      <style>{css}</style>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {search && <div style={{ flex: 1, minWidth: 0 }}>{search}</div>}
        {!inline && (
          <Badge count={active} size="small" offset={[-4, 4]}>
            <Button icon={<FilterOutlined />} onClick={() => setOpen(true)}>
              {t("m_filters")}
            </Button>
          </Badge>
        )}
      </div>
      {inline && children}
      {inline && active > 0 && resetBtn}
      {!inline && (
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          placement="bottom"
          height="auto"
          title={t("m_filters")}
          styles={{ body: { display: "grid", gap: 12, maxHeight: "70vh", overflow: "auto" } }}
          footer={
            <div style={{ display: "flex", gap: 8 }}>
              {onReset && (
                <Button block size="large" onClick={onReset}>
                  {t("reset")}
                </Button>
              )}
              <Button block size="large" type="primary" onClick={() => setOpen(false)}>
                {t("m_apply")}
              </Button>
            </div>
          }
        >
          <div className="ap-stack" style={{ display: "grid", gap: 12 }}>
            {children}
          </div>
        </Drawer>
      )}
    </div>
  );
}

export interface PageAction {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  onClick: () => void;
  /** Azione principale: resta un bottone anche su mobile. */
  primary?: boolean;
  hidden?: boolean;
}

/** Azioni di pagina: desktop = bottoni; mobile = azione principale + menu «...» con le altre. */
export function PageActions({ items }: { items: PageAction[] }) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const list = items.filter((i) => !i.hidden);
  if (!mobile) {
    return (
      <Space size={8} wrap>
        {list.map((i) => (
          <Button key={i.key} type={i.primary ? "primary" : "default"} icon={i.icon} onClick={i.onClick}>
            {i.label}
          </Button>
        ))}
      </Space>
    );
  }
  const main = list.filter((i) => i.primary);
  const rest = list.filter((i) => !i.primary);
  return (
    <Space size={8}>
      {main.map((i) => (
        <Button key={i.key} type="primary" icon={i.icon} onClick={i.onClick}>
          {i.label}
        </Button>
      ))}
      {rest.length > 0 && (
        <Dropdown trigger={["click"]} placement="bottomRight" menu={{ items: rest.map((i) => ({ key: i.key, icon: i.icon, label: i.label, onClick: i.onClick })) }}>
          <Button icon={<EllipsisOutlined />} aria-label={t("m_actions")} />
        </Dropdown>
      )}
    </Space>
  );
}
